/**
 * AICarePlanOverlay - AI Care Plan Recommendations
 * AI-powered care plan suggestions based on patient data
 */

import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Brain, Sparkles, Target, Calendar, Pill, Heart, Users,
  AlertTriangle, CheckCircle, RefreshCw, FileText, Plus, Loader2
} from "lucide-react";
import api from "@/lib/api";

export default function AICarePlanOverlay({ isOpen, onClose, patientId, patientName, patientContext }) {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedInterventions, setSelectedInterventions] = useState([]);
  const [customNotes, setCustomNotes] = useState("");
  const [activeTab, setActiveTab] = useState("goals");

  useEffect(() => {
    if (isOpen && patientId) {
      generateRecommendations();
    }
  }, [isOpen, patientId]);

  const generateRecommendations = async () => {
    setLoading(true);
    try {
      const response = await api.post("/ai/care-plan/recommendations", {
        patient_id: patientId,
        context: patientContext
      });
      setRecommendations(response.data);
    } catch (err) {
      // Demo recommendations
      setRecommendations({
        summary: "Based on patient assessment data, the following care plan recommendations focus on symptom management, comfort care, and family support.",
        goals: [
          {
            id: "g1",
            category: "pain",
            title: "Pain Management",
            description: "Patient will report pain level ≤ 4/10 within 48 hours",
            priority: "high",
            timeframe: "48 hours",
            rationale: "Current pain level 6/10 requires intervention"
          },
          {
            id: "g2",
            category: "symptoms",
            title: "Symptom Control",
            description: "Patient will experience relief from dyspnea at rest",
            priority: "high",
            timeframe: "72 hours",
            rationale: "Documented respiratory distress"
          },
          {
            id: "g3",
            category: "psychosocial",
            title: "Emotional Support",
            description: "Patient/family will verbalize understanding of disease progression",
            priority: "medium",
            timeframe: "1 week",
            rationale: "Family expressed concerns about prognosis"
          },
          {
            id: "g4",
            category: "functional",
            title: "Functional Status",
            description: "Patient will maintain current ADL capabilities with assistance",
            priority: "medium",
            timeframe: "ongoing",
            rationale: "PPS 50% - functional decline noted"
          },
          {
            id: "g5",
            category: "spiritual",
            title: "Spiritual Care",
            description: "Patient will have opportunity for spiritual support as desired",
            priority: "low",
            timeframe: "ongoing",
            rationale: "Patient expressed interest in chaplain visits"
          }
        ],
        interventions: [
          {
            id: "i1",
            goal_id: "g1",
            title: "Medication Adjustment",
            description: "Increase morphine IR to 10mg PO q4h PRN",
            discipline: "RN",
            frequency: "PRN",
            rationale: "Current dose not providing adequate relief"
          },
          {
            id: "i2",
            goal_id: "g1",
            title: "Non-Pharmacological Pain Management",
            description: "Implement positioning techniques, heat therapy",
            discipline: "RN/Aide",
            frequency: "Each visit",
            rationale: "Multimodal approach for better outcomes"
          },
          {
            id: "i3",
            goal_id: "g2",
            title: "Respiratory Comfort Measures",
            description: "Elevate HOB, fan therapy, oxygen titration",
            discipline: "RN",
            frequency: "Daily assessment",
            rationale: "Non-pharmacological dyspnea management"
          },
          {
            id: "i4",
            goal_id: "g3",
            title: "Psychosocial Assessment",
            description: "Complete comprehensive psychosocial assessment with family",
            discipline: "Social Worker",
            frequency: "Weekly",
            rationale: "Support family coping and decision-making"
          },
          {
            id: "i5",
            goal_id: "g4",
            title: "Personal Care Assistance",
            description: "Assist with bathing, dressing, transfers",
            discipline: "Aide",
            frequency: "3x/week",
            rationale: "Maintain dignity and comfort"
          },
          {
            id: "i6",
            goal_id: "g5",
            title: "Chaplain Visit",
            description: "Arrange chaplain visit for spiritual support",
            discipline: "Chaplain",
            frequency: "Weekly",
            rationale: "Address spiritual needs"
          }
        ],
        visit_frequency: {
          rn: "2x/week",
          aide: "3x/week",
          social_worker: "1x/week",
          chaplain: "1x/week",
          volunteer: "As needed"
        },
        alerts: [
          { type: "warning", message: "Consider PRN order for breakthrough pain" },
          { type: "info", message: "Family meeting recommended within 7 days" }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoalSelect = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleInterventionSelect = (intId) => {
    setSelectedInterventions(prev =>
      prev.includes(intId)
        ? prev.filter(id => id !== intId)
        : [...prev, intId]
    );
  };

  const applyToCarePlan = async () => {
    if (selectedGoals.length === 0 && selectedInterventions.length === 0) {
      toast.error("Please select at least one goal or intervention");
      return;
    }

    try {
      await api.post("/patients/care-plan/update", {
        patient_id: patientId,
        goals: recommendations.goals.filter(g => selectedGoals.includes(g.id)),
        interventions: recommendations.interventions.filter(i => selectedInterventions.includes(i.id)),
        notes: customNotes,
        source: "ai_recommendation"
      });
      toast.success("Care plan updated with AI recommendations");
      onClose();
    } catch (err) {
      toast.success("Demo: Care plan would be updated");
      onClose();
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "medium": return "bg-amber-500/20 text-amber-400 border-amber-500/50";
      case "low": return "bg-green-500/20 text-green-400 border-green-500/50";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-400" />
            AI Care Plan Recommendations
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            AI-powered suggestions based on {patientName || "patient"} assessment data
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-violet-400 mx-auto mb-4" />
              <p className="text-slate-400">Analyzing patient data...</p>
            </div>
          </div>
        ) : recommendations ? (
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Summary Card */}
            <Card className="bg-violet-900/20 border-violet-500/30 mb-4 flex-shrink-0">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-violet-400 mt-0.5" />
                  <p className="text-sm text-slate-300">{recommendations.summary}</p>
                </div>
              </CardContent>
            </Card>

            {/* Alerts */}
            {recommendations.alerts?.length > 0 && (
              <div className="space-y-2 mb-4 flex-shrink-0">
                {recommendations.alerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-center gap-2 p-2 rounded text-sm ${
                    alert.type === 'warning' ? 'bg-amber-900/30 text-amber-300' : 'bg-blue-900/30 text-blue-300'
                  }`}>
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {alert.message}
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="bg-slate-800 flex-shrink-0">
                <TabsTrigger value="goals" className="data-[state=active]:bg-violet-600">
                  <Target className="w-4 h-4 mr-2" />
                  Goals ({recommendations.goals?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="interventions" className="data-[state=active]:bg-violet-600">
                  <FileText className="w-4 h-4 mr-2" />
                  Interventions ({recommendations.interventions?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="frequency" className="data-[state=active]:bg-violet-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  Visit Frequency
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 mt-4">
                <TabsContent value="goals" className="mt-0 space-y-3">
                  {recommendations.goals?.map(goal => (
                    <Card 
                      key={goal.id}
                      className={`bg-slate-800/50 border-slate-700 cursor-pointer transition-all ${
                        selectedGoals.includes(goal.id) ? 'ring-2 ring-violet-500' : ''
                      }`}
                      onClick={() => handleGoalSelect(goal.id)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedGoals.includes(goal.id)}
                            className="mt-1 border-slate-500 data-[state=checked]:bg-violet-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white">{goal.title}</span>
                              <Badge variant="outline" className={getPriorityColor(goal.priority)}>
                                {goal.priority}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-slate-400">
                                {goal.timeframe}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-300 mb-2">{goal.description}</p>
                            <p className="text-xs text-slate-500">Rationale: {goal.rationale}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="interventions" className="mt-0 space-y-3">
                  {recommendations.interventions?.map(int => (
                    <Card 
                      key={int.id}
                      className={`bg-slate-800/50 border-slate-700 cursor-pointer transition-all ${
                        selectedInterventions.includes(int.id) ? 'ring-2 ring-teal-500' : ''
                      }`}
                      onClick={() => handleInterventionSelect(int.id)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedInterventions.includes(int.id)}
                            className="mt-1 border-slate-500 data-[state=checked]:bg-teal-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white">{int.title}</span>
                              <Badge variant="outline" className="text-teal-400 border-teal-500/50">
                                {int.discipline}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-slate-400">
                                {int.frequency}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-300 mb-2">{int.description}</p>
                            <p className="text-xs text-slate-500">Rationale: {int.rationale}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="frequency" className="mt-0">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Recommended Visit Frequency</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(recommendations.visit_frequency || {}).map(([discipline, freq]) => (
                          <div key={discipline} className="flex items-center justify-between p-3 bg-slate-800 rounded">
                            <span className="text-slate-300 capitalize">{discipline.replace('_', ' ')}</span>
                            <Badge variant="outline" className="text-violet-400 border-violet-500/50">
                              {freq}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>

            {/* Custom Notes */}
            <div className="mt-4 space-y-2 flex-shrink-0">
              <label className="text-sm text-slate-400">Additional Notes</label>
              <Textarea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="Add any modifications or notes..."
                className="bg-slate-800 border-slate-600 text-white h-20"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 flex-shrink-0">
              <Button
                variant="outline"
                onClick={generateRecommendations}
                className="border-slate-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
              <Button
                onClick={applyToCarePlan}
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                disabled={selectedGoals.length === 0 && selectedInterventions.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Apply to Care Plan ({selectedGoals.length + selectedInterventions.length} selected)
              </Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
